// Client-side mirror of the zod schema in `worker/admin/src/routes/intake.ts`
// (V2.0 register_order). Kept in sync by hand — the worker remains the
// authoritative validator (re-runs zod + validateOrder server-side). This
// copy exists for inline UX feedback in the admin "Ny ordre" form.
//
// If you change the canonical schema in the worker, change it here too.

import { z } from 'zod';
import type { OrderDraft } from '@jywer/intake';

const rateDraftSchema = z.object({
  rate_number: z.number().int().positive(),
  amount: z.number().nonnegative(),
  trigger_event: z.enum([
    'ved_indgaaelse',
    'opstartsmoede',
    'optagelsesdagen',
    'design_godkendt',
    'sidste_revision',
    'ved_revision',
    'levering',
    'faktureret',
    'custom',
  ]),
  notes: z.string().optional(),
});

const v2ProjectProductFields = {
  delivery_deadline: z.string().date().optional(),
  kickoff_date: z.string().date().optional(),
  arspakke_hint: z
    .object({
      client_arspakke_number: z.number().int().positive(),
      total_videos: z.number().int().positive(),
      year_label: z.string().optional(),
    })
    .optional(),
} as const;

const shootDraftSchema = z.object({
  due_date: z.string().date().optional(),
  locations: z.array(z.string().min(1).max(200)).max(10).optional(),
});

const websiteProductSchema = z.object({
  type: z.literal('website'),
  title: z.string().optional(),
  website_price: z.number().nonnegative(),
  website_type: z.enum(['basic', 'template', 'custom', 'ecommerce', 'other']),
  rate_structure: z.enum(['1_rate', '2_rater_50_50', '3_rater', 'specialaftale']),
  rates: z.array(rateDraftSchema).optional(),
  has_basic_abo: z.boolean(),
  basic_abo_contents: z
    .array(
      z.enum([
        'hosting_af_domaene',
        'webflow_hosting',
        'fri_telefonsupport',
        'cookiebanner',
        'weglot',
        'vimeo_pro',
        'jetboost',
        'webflow_localization',
      ]),
    )
    .optional(),
  basic_abo_monthly_price: z.number().nonnegative().optional(),
  basic_abo_billing_period: z.enum(['monthly', 'quarterly', 'halvaarlig', 'yearly']).optional(),
  client_website_url: z.string().optional(),
  ...v2ProjectProductFields,
});

const videoProductSchema = z.object({
  type: z.literal('video'),
  title: z.string().optional(),
  video_price: z.number().nonnegative(),
  filmtype: z
    .enum([
      'profilvideo',
      'employer_branding',
      'testimonial',
      'produktvideo',
      'content_pakke',
      'anden_video',
    ])
    .optional(),
  locations: z.string().optional(),
  rate_structure: z.enum(['1_rate', '2_rater_50_50', '3_rater', 'specialaftale']),
  rates: z.array(rateDraftSchema).optional(),
  shoots: z.array(shootDraftSchema).max(20).optional(),
  ...v2ProjectProductFields,
});

const klippekortProductSchema = z.object({
  type: z.literal('klippekort'),
  title: z.string().optional(),
  klippekort_price: z.number().nonnegative(),
  total_hours_purchased: z.number().nonnegative(),
});

const pakkeChildSchema = z.discriminatedUnion('type', [websiteProductSchema, videoProductSchema]);

const pakkelosningProductSchema = z.object({
  type: z.literal('pakkelosning'),
  title: z.string().optional(),
  total_value: z.number().nonnegative(),
  rate_structure: z.enum(['1_rate', '2_rater_50_50', '3_rater', 'specialaftale']),
  rates: z.array(rateDraftSchema).optional(),
  package_billed_as_single_line: z.boolean(),
  children: z.array(pakkeChildSchema),
  ...v2ProjectProductFields,
});

const subscriptionProductSchema = z.object({
  type: z.literal('subscription'),
  subscription_type: z.enum([
    'basic_hosting_support',
    'seo_aeo',
    'google_ads',
    'meta_ads',
    'linkedin_ads',
    'linkedin_profilpleje',
    'maas',
    'other',
  ]),
  monthly_price: z.number().nonnegative(),
  setup_fee: z.number().nonnegative().optional(),
  billing_period: z.enum(['monthly', 'quarterly', 'halvaarlig', 'yearly']).optional(),
  contract_length_months: z.number().int().positive().nullable().optional(),
  start_date: z.string().optional(),
  setup_trigger_task_id: z.string().optional(),
  description: z.string().optional(),
  trigger_event: z
    .enum([
      'ved_indgaaelse',
      'ved_opstart',
      'ved_levering',
      'design_godkendt',
      'optagelsesdagen',
      'custom',
    ])
    .optional(),
});

const productSchema = z.discriminatedUnion('type', [
  websiteProductSchema,
  videoProductSchema,
  klippekortProductSchema,
  pakkelosningProductSchema,
  subscriptionProductSchema,
]);

export const orderDraftSchema = z.object({
  client_name: z.string().min(1, 'Kundenavn er påkrævet'),
  client_vat: z.string().optional(),
  is_new_client_explicit: z.boolean().optional(),
  invoice_email: z.string().email('Ugyldig email'),
  sold_at: z.string().optional(),
  lead_source: z.enum(['cold_new_client', 'inbound_new_client', 'existing_client']),
  client_website_url: z.string().optional(),
  sales_rep_id: z.string().uuid().optional(),
  extra_sales_rep_id: z.string().uuid().optional(),
  extra_sales_rep_share_pct: z.union([z.literal(25), z.literal(50), z.literal(75)]).optional(),
  deadline_type: z.enum(['ingen', 'blod', 'hard']).optional(),
  deadline_date: z.string().optional(),
  products: z.array(productSchema).min(1, 'Mindst ét produkt skal tilføjes'),
  email_custom_text: z.string().optional(),

  // V2.0 order metadata.
  order_summary_md: z.string().max(20_000).optional(),
  pipeline_clickup_task_id: z.string().max(64).optional(),
  orderer_name: z.string().max(200).optional(),
  orderer_email: z.string().max(320).optional(),
  orderer_phone: z.string().max(64).optional(),
  add_on_cross_sales_notes: z.string().max(10_000).optional(),
  client_marketing_intel: z.string().max(10_000).optional(),
  signed_contract_r2_key: z
    .string()
    .max(512)
    .regex(/^orders\/[0-9a-f-]{36}\/[A-Za-z0-9._-]+$/)
    .optional(),
});

export type ParsedOrderDraft = z.infer<typeof orderDraftSchema>;

// Compile-time check: the schema's inferred output is assignable to the
// canonical OrderDraft type. Drift (missing fields, type mismatches) shows
// up as a tsc error here.
export type OrderDraftCompat = ParsedOrderDraft extends OrderDraft ? true : never;
